import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { useSettings } from "../context/SettingsContext";
import { money, nextSunday } from "../lib/api";

export default function Transparency() {
  const { settings } = useSettings();
  const L = settings?.ledger;
  const updates = settings?.updates || [];

  return (
    <>
      <PageHero
        eyebrow="Transparency & Impact"
        lines={[
          { text: "Sunday" },
          { text: "updates", className: "font-serifg italic font-semibold" },
        ]}
        lead="Kamalama Foundation posts a Transparency & Impact Update every Sunday with verified information about funds raised, costs, funds available for the program, assessment progress, and, once awards begin, funds distributed and businesses assisted."
      />

      <section className="wrap py-16 sm:py-20">
        <div className="grid gap-10 lg:grid-cols-2">
          <Reveal className="flex flex-col gap-5" data-testid="transparency-ledger">
            <div className="flex flex-col gap-2">
              <p className="eyebrow">Assessment & fundraising stage</p>
              <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
                Funds
              </h2>
            </div>
            <div className="card-surface">
              <p className="font-mono text-xs text-muted-foreground">
                {L ? `Update of Sunday, ${L.date}` : `First update: ${nextSunday()}`}
              </p>
              <table className="ledger-table">
                <tbody>
                  <tr>
                    <th>Total funds raised</th>
                    <td>{L ? money(L.raised) : "—"}</td>
                  </tr>
                  <tr>
                    <th>Processing & fundraising expenses</th>
                    <td>{L ? money(L.expenses) : "—"}</td>
                  </tr>
                  <tr className="total">
                    <th>Net funds available for the program</th>
                    <td>{L ? money(L.raised - L.expenses) : "—"}</td>
                  </tr>
                </tbody>
              </table>
              <p className="text-sm text-muted-foreground">
                Net funds are computed as raised minus expenses. Every figure
                is checked by a second person before posting.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.12} className="flex flex-col gap-5">
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Program updates
            </h2>
            <div className="card-surface" data-testid="transparency-updates">
              <p className="font-mono text-xs text-muted-foreground">
                {L ? `Sunday, ${L.date}` : `As of ${settings?.resources_updated || "October 1, 2026"}`}
              </p>
              <ul className="flex list-disc flex-col gap-2 pl-5">
                {updates.map((u, i) => (
                  <li key={i}>{u}</li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-border bg-muted/40">
        <div className="wrap flex flex-col gap-6 py-16 sm:py-20">
          <Reveal className="flex flex-col gap-3">
            <p className="eyebrow">After awards begin</p>
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Awards & distribution
            </h2>
            <p className="max-w-3xl leading-relaxed text-muted-foreground">
              These figures will appear once the Board adopts a written
              assistance policy and awards begin. Recipient business names and
              individual award amounts will be published only with written
              recipient consent.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <table className="ledger-table max-w-2xl" data-testid="awards-table">
              <tbody>
                {[
                  "Final eligibility criteria",
                  "Award and distribution methodology",
                  "Formal applications received",
                  "Businesses approved for assistance",
                  "Business rent / lease assistance",
                  "Household housing stabilization",
                  "Supplemental assistance",
                  "Total funds distributed",
                ].map((row) => (
                  <tr key={row}>
                    <th>{row}</th>
                    <td>—</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Reveal>
        </div>
      </section>
    </>
  );
}
